import UIComponent from "sap/ui/core/UIComponent";
import Device from "sap/ui/Device";
import { SimpleCacheBuster } from "./util/SimpleCacheBuster";
import "reflect-metadata";
import JSONModel from "sap/ui/model/json/JSONModel";
import { AuthGuardService, GuardConfig } from "./service/auth/authGuardService";
import { RoleEnum, UserToken } from "@ang/shared-types";
import HashChanger from "sap/ui/core/routing/HashChanger";
import { getAuthModel, setAuthFromToken } from "./model/authStore";

/**
 * @namespace ang.order_portal
 */
export default class Component extends UIComponent {
	public static readonly metadata = {
		manifest: "json",
		interfaces: ["sap.ui.core.IAsyncContentCreation"],
	};

	private contentDensityClass: string;
	private guard!: AuthGuardService;

	public init(): void {
		SimpleCacheBuster.init();
		(window as any).SimpleCacheBuster = SimpleCacheBuster;

		// Use existing auth store model
		const auth = getAuthModel();

		// Hydrate from sessionStorage if token exists
		const stored = sessionStorage.getItem("jwt");
		if (stored) {
			try {
				setAuthFromToken(stored);
			} catch {
				// invalid token → clear
				sessionStorage.removeItem("jwt");
			}
		}

		this.setModel(auth, "auth");
		const lang = localStorage.getItem("ui5.language") || "en";
		this.setModel(new JSONModel({ language: lang }), "settingsModel");

		// Base init
		super.init();

		// Create guard AFTER model exists
		this.guard = new AuthGuardService(() => this.getModel("auth") as JSONModel);

		// Router guard - Override the targets display method for better control
		const router = this.getRouter();
		const routes = this.getManifestEntry("/sap.ui5/routing/routes") as any[];
		const targets = router.getTargets();
		const originalDisplay = targets.display.bind(targets);

		// Override the targets display method to implement auth guard
		targets.display = (targetName: string | string[], ...args: any[]) => {
			// Find the route that uses this target
			const targetNames = Array.isArray(targetName) ? targetName : [targetName];

			for (const name of targetNames) {
				const route = routes.find((r) => {
					const target = r.target;
					return (
						target === name || (Array.isArray(target) && target.includes(name))
					);
				});

				if (route?.xGuard) {
					const cfg = route.xGuard as GuardConfig;

					if (!this.guard.check(cfg)) {
						const denyRoute = this.guard.isAuthenticated()
							? "landingPage"
							: "auth";

						// Display the deny route instead
						return originalDisplay(denyRoute, ...args);
					}
				}
			}

			// If we get here, the user has access - display the original target
			return originalDisplay(targetName, ...args);
		};

		// Also add route matched handler to update URL when access is denied
		router.attachRouteMatched((e: any) => {
			const name = e.getParameter("name") as string;
			const cfg = routes.find((r) => r.name === name)?.xGuard as
				| GuardConfig
				| undefined;

			if (cfg && !this.guard.check(cfg)) {
				const denyRoute = this.guard.isAuthenticated() ? "landingPage" : "auth";

				// Update the URL to match the redirected view
				setTimeout(() => {
					router.navTo(denyRoute, {}, true);
				}, 0);
			}
		});

		router.initialize();
	}

	public getContentDensityClass(): string {
		if (this.contentDensityClass === undefined) {
			if (
				document.body.classList.contains("sapUiSizeCozy") ||
				document.body.classList.contains("sapUiSizeCompact")
			) {
				this.contentDensityClass = "";
			} else if (!Device.support.touch) {
				this.contentDensityClass = "sapUiSizeCompact";
			} else {
				this.contentDensityClass = "sapUiSizeCozy";
			}
		}
		return this.contentDensityClass;
	}
}
